import { Component, OnInit } from '@angular/core';
import {
  BajaRecordatorio,
  EnvioRecordatorio,
  MesRecordatorio,
  RecordatorioInactividadService,
  ResumenRecordatorio,
} from 'src/app/shared/services/recordatorio-inactividad.service';

type Pestana = 'meses' | 'envios' | 'bajas';

/** Una lista paginada con su busqueda. */
interface Listado<T> {
  items: T[];
  total: number;
  page: number;
  q: string;
  cargando: boolean;
}

const POR_PAGINA = 20;

/**
 * Recordatorio de inactividad (Marketing → Recordatorio de inactividad).
 *
 * Solo operadores, y a proposito con poco para tocar: prender o apagar,
 * mandarse la prueba y dar de baja a mano. Cuantos salen no se configura —el
 * dia 1 salen todos los que califican— ni desde que casilla.
 *
 * Tampoco hay "enviar ahora": el envio sale solo el dia 1 por el cron, y un
 * clic por error seria justo la rafaga que puede pausar la cuenta de SES.
 */
@Component({
  selector: 'app-recordatorio-admin',
  templateUrl: './recordatorio-admin.component.html',
  styleUrls: ['./recordatorio-admin.component.sass'],
})
export class RecordatorioAdminComponent implements OnInit {
  resumen: ResumenRecordatorio | null = null;
  cargando = false;
  error = '';
  aviso = '';
  ocupado = false;

  /** Confirmacion en dos pasos del interruptor. */
  confirmando: 'encender' | 'apagar' | null = null;
  emailBaja = '';

  pestana: Pestana = 'meses';
  readonly porPagina = POR_PAGINA;
  envios: Listado<EnvioRecordatorio> = this.listadoVacio();
  bajas: Listado<BajaRecordatorio> = this.listadoVacio();

  constructor(private recordatorio: RecordatorioInactividadService) {}

  ngOnInit(): void {
    void this.cargar();
  }

  async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    try {
      this.resumen = await this.recordatorio.resumen();
    } catch {
      this.error = 'No se pudo cargar el recordatorio. Probá de nuevo en un momento.';
    } finally {
      this.cargando = false;
    }
  }

  pedirInterruptor(): void {
    if (!this.resumen) return;
    this.confirmando = this.resumen.encendido ? 'apagar' : 'encender';
  }

  async confirmarInterruptor(): Promise<void> {
    if (!this.confirmando) return;
    const encender = this.confirmando === 'encender';
    await this.accion(async () => {
      this.resumen = await this.recordatorio.cambiarInterruptor(encender);
      this.aviso = encender ? 'Recordatorio encendido.' : 'Recordatorio apagado.';
    }, 'No se pudo cambiar el estado. Probá de nuevo.');
    this.confirmando = null;
  }

  async enviarPrueba(): Promise<void> {
    await this.accion(async () => {
      const r = await this.recordatorio.enviarPrueba();
      this.aviso = `Prueba enviada a ${r.enviadoA}. Revisá tu bandeja (y spam).`;
    }, 'No se pudo enviar la prueba.');
  }

  async darDeBaja(): Promise<void> {
    const email = this.emailBaja.trim();
    if (!email) return;
    await this.accion(async () => {
      const r = await this.recordatorio.darDeBaja(email);
      this.aviso = r.usuarios
        ? `${email} no va a recibir más el recordatorio.`
        : `${email} ya estaba de baja o no es de ningún cliente.`;
      this.emailBaja = '';
      this.resumen = await this.recordatorio.resumen();
      if (this.pestana === 'bajas') await this.cargarBajas(1);
    }, 'No se pudo registrar la baja. Revisá que el correo esté bien escrito.');
  }

  // ---------------------------------------------------------------- pestanas

  async cambiarPestana(p: Pestana): Promise<void> {
    this.pestana = p;
    if (p === 'envios' && !this.envios.items.length) await this.cargarEnvios(1);
    if (p === 'bajas' && !this.bajas.items.length) await this.cargarBajas(1);
  }

  async cargarEnvios(page: number): Promise<void> {
    await this.cargarListado(this.envios, page, (pg, q) =>
      this.recordatorio.envios(pg, POR_PAGINA, q)
    );
  }

  async cargarBajas(page: number): Promise<void> {
    await this.cargarListado(this.bajas, page, (pg, q) =>
      this.recordatorio.bajas(pg, POR_PAGINA, q)
    );
  }

  totalPaginas(l: Listado<unknown>): number {
    return Math.max(1, Math.ceil(l.total / POR_PAGINA));
  }

  // ---------------------------------------------------------------- vista

  pctVolvieron(m: MesRecordatorio): number | null {
    return m.enviados ? Math.round((m.volvieron14d / m.enviados) * 1000) / 10 : null;
  }

  estadoCorrida(): string {
    const c = this.resumen?.corrida;
    if (!c) return 'Todavía no corrió nunca';
    return `${c.ciclo}: ${c.estado === 'EN_CURSO' ? 'enviando' : 'terminada'}`;
  }

  origenBaja(origen: string): string {
    return origen === 'ONE_CLICK' ? 'Botón del correo' : origen === 'OPERADOR' ? 'Operador' : origen;
  }

  // ---------------------------------------------------------------- internos

  private async cargarListado<T>(
    l: Listado<T>,
    page: number,
    pedir: (page: number, q: string) => Promise<{ items: T[]; total: number; page: number }>
  ): Promise<void> {
    l.cargando = true;
    try {
      const r = await pedir(page, l.q);
      l.items = r.items;
      l.total = r.total;
      l.page = r.page;
    } catch {
      this.error = 'No se pudo cargar la lista. Probá de nuevo.';
    } finally {
      l.cargando = false;
    }
  }

  private listadoVacio<T>(): Listado<T> {
    return { items: [], total: 0, page: 1, q: '', cargando: false };
  }

  private async accion(fn: () => Promise<void>, mensajeError: string): Promise<void> {
    this.ocupado = true;
    this.error = '';
    this.aviso = '';
    try {
      await fn();
    } catch {
      this.error = mensajeError;
    } finally {
      this.ocupado = false;
    }
  }
}
